import axios, { AxiosError, AxiosResponse } from 'axios';
import { authService } from './auth.service';
import { ApiError } from '@/utils/api-response.util';
import { toastService } from './toast.service';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3200/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Функція для отримання base URL
export const getApiBaseUrl = (): string => {
  return api.defaults.baseURL || '';
};

// Request interceptor - додає токен та мову до кожного запиту
api.interceptors.request.use(
  config => {
    // Додаємо токен з authService
    const token = authService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Додаємо мову з localStorage
    const language = localStorage.getItem('language') || 'uk';
    config.headers['X-Locale'] = language;

    // Додаємо активну панель з localStorage (не перезаписуємо, якщо вже виставлено явно)
    const sidebarMode = localStorage.getItem('sidebar-mode');
    if (sidebarMode && !config.headers['X-Panel']) {
      config.headers['X-Panel'] = sidebarMode;
    }

    // Передаємо варіант UI для A/B аналітики
    const uiVariant = localStorage.getItem('ui-variant') || 'a';
    config.headers['X-UI-Variant'] = uiVariant;

    // Часова зона клієнта
    config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor - обробка помилок
// Дедуплікація пачки 401 (див. обробку 401 нижче).
const UNAUTHORIZED_BURST_MS = 2000;
let unauthorizedBurstActive = false;

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const { response } = error;
    const statusCode = response?.status;
    const responseData = response?.data as any;

    // Покращена логіка вилучення повідомлення про помилку
    let message = 'Сталася помилка'; // Повідомлення за замовчуванням
    if (responseData) {
      if (responseData.message) {
        message = responseData.message;
      } else if (responseData.error) { // Додано перевірку на поле 'error'
        message = responseData.error;
      }
    }

    // Обробка 401 - неавторизований доступ
    if (statusCode === 401) {
      // Якщо impersonation сесія відкликана/закінчилась — повертаємо адмін-акаунт без logout
      if (message === 'Impersonation session revoked or expired') {
        authService.clearImpersonationData();
        localStorage.setItem('sidebar-mode', 'admin');
        toastService.warning('Сесію перегляду завершено.');
        window.dispatchEvent(new CustomEvent('impersonation:revoked'));
        return Promise.reject(new ApiError(message, statusCode, responseData));
      }

      const isAuthPage = window.location.pathname.startsWith('/auth');
      const isExactAuthPage = window.location.pathname === '/auth';
      const isRegisterRequest = error.config?.url?.endsWith('/register');

      if (!isAuthPage || !isRegisterRequest) {
        // Кілька паралельних запитів з протухлим токеном дають пачку 401 в ту саму мілісекунду -
        // очищення токенів, редірект і лог робимо ОДИН раз на пачку (вікно UNAUTHORIZED_BURST_MS),
        // а не по разу на кожен запит (було 4 однакові error-записи в BetterStack за 230 мс).
        // Це очікувана ситуація (закінчилась сесія), тож warn, а не error.
        if (!unauthorizedBurstActive) {
          unauthorizedBurstActive = true;
          setTimeout(() => { unauthorizedBurstActive = false; }, UNAUTHORIZED_BURST_MS);

          console.warn('API: 401 Unauthorized. Clearing tokens.');
          authService.clearAllAuthData();
          if (!isExactAuthPage) {
            window.location.replace('/auth');
          }
        }
      }
    }

    // Обробка 500 - Internal Server Error
    if (statusCode === 500) {
      toastService.error('Виникла помилка на сервері. Будь ласка, спробуйте пізніше.');
    }

    // 401 - очікувана подія (сесія закінчилась), вже залогована вище одним warn на пачку.
    if (statusCode !== 401) {
      console.error('API Error:', message, statusCode ? `(${statusCode})` : '');
    }

    // Створюємо кастомну помилку ApiError
    const apiError = new ApiError(message, statusCode, responseData);

    return Promise.reject(apiError);
  }
);

export default api;
